import React, { useState } from "react";
import { createPortal } from "react-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { followCitySignup } from "../../util/adminApi";
import { RECAPTCHA_SITEKEY } from "../../config";

const FOLLOW_CITY_KEY_PREFIX = "vegifyit_follow_city_";

export const hasFollowedCity = (cityName) => {
  try {
    return !!localStorage.getItem(FOLLOW_CITY_KEY_PREFIX + cityName);
  } catch {
    return false;
  }
};

const storeFollowedCity = (cityName) => {
  try {
    localStorage.setItem(FOLLOW_CITY_KEY_PREFIX + cityName, Date.now().toString());
  } catch {
    // silently fail
  }
};

const FollowCityModal = ({ cityName, state, lat, lng, onClose }) => {
  const [email, setEmail] = useState("");
  const [radiusMiles, setRadiusMiles] = useState(10);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!recaptchaToken) {
      setError("Please complete the reCAPTCHA");
      return;
    }

    setSubmitting(true);
    try {
      const resp = await followCitySignup({
        email,
        location: { lat, lng },
        city: cityName,
        recaptchaToken,
        preferences: {
          radiusMiles,
          cityName,
          state,
          lat,
          lng,
        },
      });

      if (resp?.response?.status === 400) {
        setError(
          resp.response.data.errors.map((obj) => obj.msg).join(", ")
        );
      } else if (resp?.response?.status >= 500) {
        setError("Something went wrong. Please try again.");
      } else {
        setSuccess(true);
        storeFollowedCity(cityName);
        setTimeout(() => onClose(), 2500);
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const displayName = state ? `${cityName}, ${state}` : cityName;

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button
          className="modal-close"
          onClick={onClose}
          aria-label="Close modal"
        >
          &times;
        </button>

        {success ? (
          <>
            <i
              className="fas fa-check-circle modal-icon"
              style={{ color: "var(--color-primary)" }}
            ></i>
            <h3 className="modal-title">You're following {displayName}!</h3>
            <p className="modal-message">
              We'll let you know when new vegan restaurants are added nearby.
            </p>
          </>
        ) : (
          <>
            <i
              className="fas fa-bell modal-icon"
              style={{ color: "var(--color-primary)" }}
            ></i>
            <h3 className="modal-title">Follow {cityName}'s Vegan Scene</h3>
            <p className="modal-message">
              Get notified when new vegan restaurants and dishes are added near you.
            </p>
            <form onSubmit={handleSubmit} className="follow-city-form">
              <div className="follow-city-field">
                <label htmlFor="follow-email">Email</label>
                <input
                  id="follow-email"
                  type="email"
                  className="text-form"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div className="follow-city-field">
                <label htmlFor="follow-radius">Notify me within</label>
                <select
                  id="follow-radius"
                  className="text-form"
                  value={radiusMiles}
                  onChange={(e) => setRadiusMiles(Number(e.target.value))}
                >
                  <option value={5}>5 miles</option>
                  <option value={10}>10 miles</option>
                  <option value={25}>25 miles</option>
                </select>
              </div>
              <div className="follow-city-recaptcha">
                <ReCAPTCHA
                  sitekey={RECAPTCHA_SITEKEY}
                  onChange={setRecaptchaToken}
                />
              </div>
              {error && <p className="follow-city-error">{error}</p>}
              <button
                type="submit"
                className="btn-primary"
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Follow This City"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default FollowCityModal;
